import { EditorState } from 'draft-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorProps } from 'react-draft-wysiwyg';
import { editorStateToHtml, htmlToEditorState } from '../../utils';

export const useRichTextEditor = ({ value, defaultValue, onChange, onChangeValue }) => {
	const [editorState, setEditorState] = useState<EditorState>(htmlToEditorState(value));
	const [isTextMode, setIsTextMode] = useState(false);
	const [plainText, setPlainText] = useState('');
	const editorRef = useRef(null);

	useEffect(() => {
		if (value) setEditorState(htmlToEditorState(value));
	}, [value]);

	const defaultEditorState = useMemo(() => htmlToEditorState(defaultValue), [defaultValue]);

	const onEditorStateChange = useCallback<EditorProps['onEditorStateChange']>(
		(newState) => {
			setEditorState(newState as EditorState);
			const html = editorStateToHtml(newState as EditorState);
			onChange?.(html);
			onChangeValue?.(html);
		},
		[onChange, onChangeValue]
	);

	const handlePlainTextChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const text = e.target.value;
			setPlainText(text);
			onChange?.(text);
			onChangeValue?.(text);
			setEditorState(htmlToEditorState(text));
		},
		[onChange, onChangeValue, setPlainText, setEditorState]
	);

	const handleModeSwitch = useCallback(() => {
		let value = plainText;
		if (!isTextMode) {
			value = editorRef.current?.getEditorState()?.getCurrentContent()?.getPlainText();
			setPlainText(value);
		}
		onChange?.(value);
		onChangeValue?.(value);
		setIsTextMode((prev) => !prev);
	}, [plainText, isTextMode, onChange, onChangeValue, setPlainText, setIsTextMode]);

	/**
	 * `react-draft-wysiwyg` uses Object.hasProperty() check to decide controlled/uncontrolled state,
	 * it considers the state to be controlled even if it's undefined ¯\_(ツ)_/¯
	 * So, we will only pass the state props if they are defined
	 */
	const editorProps = useMemo(() => {
		const stateProps: any = {};
		if (typeof editorState !== 'undefined') {
			stateProps.editorState = editorState;
		}
		if (typeof defaultEditorState !== 'undefined') {
			stateProps.defaultEditorState = defaultEditorState;
		}
		return stateProps;
	}, [defaultEditorState, editorState]);

	return {
		editorProps,
		isTextMode,
		setIsTextMode,
		plainText,
		setPlainText,
		editorRef,
		handlePlainTextChange,
		onEditorStateChange,
		handleModeSwitch,
	};
};
